import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Bell } from 'lucide-react'
import NotificationPanel from './NotificationPanel'
import { notificationService, AppNotification } from '@/services/notificationService'

const NotificationBell: React.FC = () => {
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const refresh = useCallback(async () => {
    try {
      const data = await notificationService.getNotifications()
      setNotifications(Array.isArray(data) ? data : [])
    } catch {
      // Notifications are non-critical; fail silently in the header
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  // Close the panel on outside click
  useEffect(() => {
    if (!isOpen) return
    const onClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [isOpen])

  const unreadCount = notifications.filter((n) => !n.isRead).length

  const handleToggle = () => {
    const next = !isOpen
    setIsOpen(next)
    if (next) {
      refresh()
    }
  }

  const handleMarkRead = async (notificationId: string) => {
    // optimistic update
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
    )
    try {
      await notificationService.markRead(notificationId)
    } catch {
      refresh()
    }
  }

  const handleMarkAllRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })))
    try {
      await notificationService.markAllRead()
    } catch {
      refresh()
    }
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={handleToggle}
        aria-label="Notifications"
        className="relative p-2 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span
            data-testid="unread-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-red-500 text-white text-xs font-medium rounded-full"
          >
            {unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <NotificationPanel
          notifications={notifications}
          onMarkRead={handleMarkRead}
          onMarkAllRead={handleMarkAllRead}
        />
      )}
    </div>
  )
}

export default NotificationBell
